import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackFunnelEvent } from '../utils/AnalyticsService';
import { jobAlertConfig } from '../data/jobAlertConfig';

const JobAlertToast = () => {
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  // --- 1. Exclude from Test page ---
  const isTestPage = location.pathname.includes('/start');

  useEffect(() => {
    if (isTestPage) {
      setShouldRender(false);
      return;
    }

    // Check if dismissed in this session
    const isDismissed = sessionStorage.getItem('job_alert_dismissed');
    if (isDismissed === 'true' || !jobAlertConfig.showBar) {
      return;
    }

    // Trigger slide-in after 3.5 seconds
    setShouldRender(true);
    const showTimer = setTimeout(() => {
      setIsVisible(true);
      trackFunnelEvent('job_toast_viewed');
    }, 3500);

    return () => clearTimeout(showTimer);
  }, [isTestPage]);

  if (!shouldRender) return null;

  const handleClose = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsVisible(false);
    sessionStorage.setItem('job_alert_dismissed', 'true');
    trackFunnelEvent('job_toast_dismissed');
    // Clean up DOM after transition ends
    setTimeout(() => setShouldRender(false), 500);
  };

  const handleJoin = () => {
    trackFunnelEvent('whatsapp_clicked', { type: 'toast_alert' });
    setIsVisible(false);
    sessionStorage.setItem('job_alert_dismissed', 'true');
    setTimeout(() => setShouldRender(false), 500);
  };

  return (
    <div
      className={`fixed bottom-6 left-6 z-[9999] max-w-sm w-[calc(100vw-3rem)] bg-white border border-slate-100 rounded-2xl shadow-2xl p-4 transition-all duration-500 ease-out transform ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0 pointer-events-none'
      }`}
    >
      {/* Top Section */}
      <div className="flex justify-between items-start gap-4 mb-2">
        <div className="flex items-center gap-2">
          {/* Glowing Green Live Badge */}
          <span className="flex h-2.5 w-2.5 relative">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
          </span>
          <span className="text-[11px] font-extrabold uppercase tracking-wider text-green-600 bg-green-50 px-2 py-0.5 rounded-md">
            Live Hiring
          </span>
        </div>
        
        {/* Dismiss Button */}
        <button
          onClick={handleClose}
          className="text-slate-400 hover:text-slate-600 p-0.5 rounded-full hover:bg-slate-50 transition-colors"
          aria-label="Close notification"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      {/* Alert Content */}
      <div className="mb-4">
        <h4 className="text-sm font-bold text-slate-800 leading-snug">
          {jobAlertConfig.alertText.replace("🔥 ", "").replace("Job Alert: ", "")}
        </h4>
        <p className="text-xs text-slate-500 mt-1 leading-relaxed">
          Get BPO hiring announcements, interview question sets, and free practice PDFs directly on your phone.
        </p>
      </div>

      {/* CTA Button */}
      <a
        href={jobAlertConfig.channelUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleJoin}
        className="flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20ba59] text-white font-extrabold text-xs py-2.5 px-4 rounded-xl shadow-md shadow-green-500/10 hover:shadow-green-500/20 transition-all duration-200 transform active:scale-[0.98] w-full text-center"
      >
        <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current text-white">
          <path d="M12.031 21.428h-.011a9.882 9.882 0 0 1-5.04-1.38l-.36-.214-3.75 1.05 1.054-3.66-.235-.373A9.881 9.881 0 0 1 2.1 11.831C2.1 6.353 6.556 1.88 12.042 1.88c2.664 0 5.166 1.042 7.042 2.92a9.921 9.921 0 0 1 2.91 7.03c0 5.478-4.455 9.95-9.963 9.998zm0-17.914c-4.382 0-7.95 3.568-7.95 7.95 0 1.4.363 2.768 1.055 3.972l1.24 2.164-2.124 7.375 7.545-2.107 2.13 1.22c1.192.68 2.535 1.037 3.923 1.037h.007c4.38 0 7.947-3.568 7.947-7.95 0-2.123-.827-4.12-2.327-5.62A7.944 7.944 0 0 0 12.03 3.514zm4.27 10.978c-.234-.117-1.385-.683-1.6-.762-.214-.08-.37-.117-.527.117-.156.234-.606.762-.743.918-.136.156-.273.176-.507.059-.234-.117-.99-.364-1.885-1.16-.697-.62-1.168-1.386-1.305-1.62-.136-.234-.014-.36.103-.477.106-.106.234-.273.351-.41.117-.137.156-.234.234-.39.078-.156.039-.293-.02-.41-.059-.117-.527-1.27-.723-1.74-.191-.46-.386-.398-.527-.406-.136-.008-.293-.008-.45-.008a.866.866 0 0 0-.625.293c-.214.234-.82.802-.82 1.954 0 1.153.84 2.266.957 2.422.117.156 1.652 2.524 4 3.48.558.226.992.36 1.332.46.56.178 1.07.153 1.473.093.453-.067 1.385-.566 1.58-1.114.195-.547.195-1.016.136-1.114-.059-.098-.215-.156-.45-.273z"/>
        </svg>
        <span>Join WhatsApp Channel</span>
      </a>
    </div>
  );
};

export default JobAlertToast;
